import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { midas } from '@resurs/midas'
import { Checkbox, Select } from '@resurs/midas-react'
import { initRoles, Roles } from '../../../models/roles'
import { getText } from '../../../internal/TextLabels'
import styles from './PermissionPicker.module.scss'
import { Authorities, AuthorityLabels } from '../../../models/authorities'

const DEFAULT_ROLE = Roles.STORE_SALES

const parseRoleFromUser = user => user?.role.name ?? DEFAULT_ROLE

const parseAuthoritiesFromUser = (user, validRoles) =>
  user?.authorities ??
  validRoles.find(({ name }) => name === DEFAULT_ROLE).authorities

const AUTHORITIES = {
  [Authorities.PAY_APPLY]: [],
  [Authorities.TRANSACTIONS]: [
    Authorities.TRANSACTIONS_DEBIT,
    Authorities.TRANSACTIONS_CREDIT,
    Authorities.TRANSACTIONS_ANNUL,
  ],
  [Authorities.STATISTICS]: [],
  [Authorities.MESSAGING]: [],
  [Authorities.ACCOUNTING]: [],
  [Authorities.SUPPORT]: [],
  [Authorities.SETTINGS]: [],
  [Authorities.USER_ADMINISTRATION]: [],
}

const PermissionPicker = ({
  user,
  loggedInUser,
  validRoles,
  disabled,
  showHint,
  onChange,
}) => {
  const [currentRole, setCurrentRole] = useState(parseRoleFromUser(user))
  const [currentAuthorities, setCurrentAuthorities] = useState(
    parseAuthoritiesFromUser(user, validRoles),
  )

  useEffect(() => {
    const roleAuthorities =
      validRoles.find(({ name }) => name === currentRole)?.authorities ?? []

    onChange({
      role: currentRole,
      additionalAuthorities: currentAuthorities.filter(
        authority => !roleAuthorities.includes(authority),
      ),
      restrictedAuthorities: roleAuthorities.filter(
        authority => !currentAuthorities.includes(authority),
      ),
      authorities: currentAuthorities,
    })
  }, [currentRole, currentAuthorities])

  const [roles, setRoles] = useState([])

  useEffect(
    () => setRoles([...initRoles(validRoles, loggedInUser)]),
    [validRoles, loggedInUser],
  )

  const isChecked = authority => currentAuthorities.includes(authority)

  const isDisabled = authority =>
    disabled || !loggedInUser.authorities.includes(authority)

  const handleChangeRole = newRole => {
    setCurrentRole(newRole)
    setCurrentAuthorities(
      validRoles.find(({ name }) => name === newRole)?.authorities ?? [],
    )
  }

  const handleToggleAuthority = newAuthority => {
    const subAuthorities = AUTHORITIES[newAuthority] ?? []
    const roleAuthorities =
      validRoles.find(({ name }) => name === currentRole)?.authorities ?? []

    setCurrentAuthorities(
      currentAuthorities.includes(newAuthority)
        ? currentAuthorities.filter(oldAuthority =>
            oldAuthority === newAuthority
              ? false
              : !subAuthorities.includes(oldAuthority),
          )
        : [
            ...(currentAuthorities ?? []),
            newAuthority,
            ...subAuthorities.filter(oldAuthority =>
              roleAuthorities.includes(oldAuthority),
            ),
          ],
    )
  }

  const handleChangeAuthority = event => handleToggleAuthority(event.target.id)

  const authLabels = new AuthorityLabels()

  const renderSubAuthorities = (authorities, isParentAuthorityChecked) => (
    <div className={styles.subAuthorities}>
      {authorities.map(authority => (
        <Checkbox
          key={authority}
          id={authority}
          text={authLabels.labels[authority]}
          checked={isChecked(authority)}
          disabled={isDisabled(authority) || !isParentAuthorityChecked}
          onChange={handleChangeAuthority}
        />
      ))}
    </div>
  )

  return (
    <>
      <Select
        id="roles"
        label={getText('user.role')}
        value={currentRole}
        values={roles}
        disabled={disabled}
        onChange={handleChangeRole}
      />
      {currentRole === Roles.SUPER_USER && showHint && (
        <div className={styles.hint}>
          {getText('user.add.superUserRequiresPhoneAndEmail')}
        </div>
      )}
      <div className={midas.formLabel}>{getText('user.authorities.title')}</div>
      {Object.entries(AUTHORITIES).map(([authority, subAuthorities]) => (
        <>
          <Checkbox
            key={authority}
            id={authority}
            text={authLabels.labels[authority]}
            checked={isChecked(authority)}
            disabled={isDisabled(authority)}
            onChange={handleChangeAuthority}
          />
          {!!subAuthorities.length &&
            renderSubAuthorities(subAuthorities, isChecked(authority))}
        </>
      ))}
    </>
  )
}

PermissionPicker.propTypes = {
  user: PropTypes.object,
  loggedInUser: PropTypes.object.isRequired,
  validRoles: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  showHint: PropTypes.bool,
  onChange: PropTypes.func,
}

PermissionPicker.defaultProps = {
  user: null,
  disabled: false,
  showHint: true,
  onChange: () => undefined,
}

export default PermissionPicker
